import { ChangeDetectorRef, Component, Inject, Input } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DropdownOption } from '@cms/shared-library';
import { TranslateService } from '@ngx-translate/core';
import { CheckEligibility } from 'src/app/models/check-eligibility';
import { CourseTrainingProgramDetails } from 'src/app/models/course-training-program-details.model';
import { TrainingEligibilityForm } from 'src/app/models/training-eligibility-form.model';
import { TrainingEligibilityService } from 'src/app/services/training-eligibility/training-eligibility.service';
import { emailValidatorPattern, learnerIdValidatorPattern, phoneValidatorPattern, postalCodeValidatorPattern } from 'src/app/utils/validators.util';

declare function openNewTab(url: string): void

@Component({
    selector: 'app-eligibility-checker',
    templateUrl: './eligibility-checker.component.html',
    styleUrls: ['../worker-enrollment/worker-enrollment.component.scss', './eligibility-checker.component.scss'],
    standalone: false
})
export class EligibilityCheckerComponent {
  @Input() courseDto: CourseTrainingProgramDetails
  @Input() isSingleTeamMember: boolean = false;
  eligCheckerExpanded: boolean = true;
  identificationOption: string = 'id';
  contactOption: string = 'email';

  checkEligibility?: CheckEligibility;
  trainingEligibilityForm: TrainingEligibilityForm;
  eligibilityForm: FormGroup | undefined;
  learnMoreUrlPlaceholder: string = 'https://www.ontario.ca/page/training-joint-health-and-safety-committee-members'; //TO-DO - refactor in future when business is clear what the end point should be

  bannerType: string = '';
  shareEligibilityResults: boolean = true;

  currentLang: string = 'en';

  identificationOptions: DropdownOption[] = [
    new DropdownOption('Learner ID', 'ID de l’apprenant ', 'id'),
    new DropdownOption('Name', 'Nom ', 'name')]
  contactOptions: DropdownOption[] = [
    new DropdownOption('Email address', 'Adresse courriel', 'email'),
    new DropdownOption('Phone number', 'Numéro de téléphone ', 'phone'),
    new DropdownOption('Postal code', 'Code Postal', 'postal')]

  constructor(
    @Inject('TrainingEligibilityService') private trainingEligibilityService: TrainingEligibilityService,
    public translateService: TranslateService,
    public formBuilder: FormBuilder,
    public cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang;

    this.eligibilityForm = this.formBuilder.group({
      learnerID: new FormControl('', [Validators.maxLength(9), Validators.pattern("^[a-zA-Z0-9]*$")]),
      learnerName: new FormControl('', []),
      emailID: new FormControl('', [Validators.pattern(emailValidatorPattern)]),
      phoneNo: new FormControl('', [Validators.pattern(phoneValidatorPattern)]),
      postalCode: new FormControl('', [Validators.pattern(postalCodeValidatorPattern)])
    });
  }

  //#region Check Eligibility
  checkIfEligible() {
    this.trainingEligibilityForm = new TrainingEligibilityForm(
      this.eligibilityForm!.controls['learnerID']?.value,
      this.eligibilityForm!.controls['learnerName']?.value,
      this.eligibilityForm!.controls['emailID']?.value,
      this.eligibilityForm!.controls['phoneNo']?.value,
      this.eligibilityForm!.controls['postalCode']?.value,
      this.courseDto.TrainingProgramId, new Date());
    this.trainingEligibilityService.CheckEligibility(this.trainingEligibilityForm).subscribe(data => {
      if (data) {
        this.checkEligibility = data;
        this.bannerType = data.IsEligible ? 'success' : 'failure'
      }
      else {
        this.bannerType = 'userNotFound'
      }
    });
  }

  setIdentificationOption(option: string) {
    let tmp;

    if (option === "id") {
      this.clearValidator(this.eligibilityForm!.controls['learnerName'])
      tmp = this.eligibilityForm!.value;
      this.eligibilityForm!.controls['learnerID'].addValidators([Validators.maxLength(9), Validators.pattern(learnerIdValidatorPattern), Validators.required]);
    }
    else {
      this.clearValidator(this.eligibilityForm!.controls['learnerID'])
      tmp = this.eligibilityForm!.value;
      this.eligibilityForm!.controls['learnerName'].addValidators([Validators.maxLength(256), Validators.required]);
    }

    this.eligibilityForm!.reset();
    this.eligibilityForm!.setValue(tmp);
  }

  setContactOption(option: string) {
    let tmp;

    if (option === "email") {
      this.clearValidator(this.eligibilityForm!.controls['phoneNo'])
      this.clearValidator(this.eligibilityForm!.controls['postalCode'])
      tmp = this.eligibilityForm!.value;

      this.eligibilityForm!.controls['emailID'].addValidators([Validators.email, Validators.required]);
    }
    else if (option === "phone") {

      this.clearValidator(this.eligibilityForm!.controls['emailID'])
      this.clearValidator(this.eligibilityForm!.controls['postalCode'])
      tmp = this.eligibilityForm!.value;

      this.eligibilityForm!.controls['phoneNo'].addValidators([Validators.required, Validators.pattern(phoneValidatorPattern)]);

    }
    else {

      this.clearValidator(this.eligibilityForm!.controls['emailID'])
      this.clearValidator(this.eligibilityForm!.controls['phoneNo'])
      tmp = this.eligibilityForm!.value;
      this.eligibilityForm!.controls['postalCode'].addValidators([Validators.required, Validators.pattern(postalCodeValidatorPattern)]);
    }

    this.eligibilityForm!.reset();
    this.eligibilityForm!.setValue(tmp);
  }

  resetEligibilityForm() {
    this.eligibilityForm!.reset();
    this.bannerType = '';
  }

  linkButtonClicked(url: string) {
    url = 'https://www.ontario.ca/page/training-joint-health-and-safety-committee-members';
    openNewTab(url)
  }

  linkedButtonClickedFr(url: string){
    url = 'https://www.ontario.ca/fr/page/formation-des-membres-des-comites-mixtes-sur-la-sante-et-la-securite-au-travail'
    openNewTab(url)
  }
  WAHlinkedButtonClicked(url: string){
    url = 'https://www.ontario.ca/page/training-working-heights'
    openNewTab(url)
  }

  WAHlinkedButtonClickedFr(url: string){
    url = 'https://www.ontario.ca/fr/page/formation-pour-le-travail-en-hauteur'
    openNewTab(url)
  }
  shareEligibilityResultsChanged(val: boolean) {
    this.shareEligibilityResults = !val;
  }

  private clearValidator(fc: AbstractControl) {
    fc.clearValidators()
    fc.setValue('')
  }

}
